/* eslint-disable react-hooks/exhaustive-deps */

import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dropdown, DropdownItem } from "staysure-component-library";

import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import RichText from "@/components/atoms/rich-text/rich-text";
import withApplication from "@/components/with-application";
import withBrand from "@/components/with-brand";
import { BRAND_STAYSURE } from "@/constants";
import { useQuoteDisabled } from "@/hooks/use-quote-disabled/use-quote-disabled";
import { useRenewalReviewStateContext } from "@/providers/renewal-review-state-provider";
import { RootState } from "@/store/root-reducer";
import { updateQuote } from "@/store/slices/quote/quote.slice";
import { PaymentDetails } from "@/types/payment-details-types";
import { PostalSettingProps } from "@/types/postal-setting-types";
import { getStrokeColor } from "@/utils/organiser.utils";

import Styles from "./postal-settings.module.css";
import AreYouSurePostalModal from "./review-modals/are-you-sure-postal-modal";

function PostalSetting({
  postalTitle,
  postalContent,
  postalButtonNo,
  postalButtonYes,
  areYouSurePopModalTitle,
  areYouSurePopModalContent,
  areYouSurePopModalButtonOne,
  areYouSurePopModalButtonTwo,
  application,
  documentEmailOption,
  documentPostOption,
  brand,
}: Readonly<PostalSettingProps>) {
  const dispatch = useDispatch();
  const { current } = useWizard();

  const documentBtnRef = useRef<HTMLButtonElement>(null);

  const answerArray =
    application === "renewal"
      ? [
          { id: 0, label: postalButtonNo },
          { id: 1, label: postalButtonYes },
        ]
      : [
          { id: 0, label: documentEmailOption },
          { id: 1, label: documentPostOption },
        ];

  const [selectedAnswer, setSelectedAnswer] = useState<string>();
  const [postalSettingModalOpen, setPostalSettingModalOpen] = useState(false);
  const quote = useSelector((state: RootState) => state.quote);
  const { setPaymentDetails, paymentDetails } = useRenewalReviewStateContext();
  const isQuoteDisabled = useQuoteDisabled();
  useEffect(() => {
    if (!quote?.attributes?.paymentDetails?.postageOption) {
      setSelectedAnswer("0");
    } else {
      setSelectedAnswer(quote?.attributes?.paymentDetails?.postageOption);
    }
  }, []);

  useEffect(() => {
    if (application === "renewal") {
      setPaymentDetails({
        ...(quote.attributes.paymentDetails as PaymentDetails),
        postageOption: selectedAnswer ?? "0",
      });
    } else if (
      quote?.attributes?.paymentDetails?.postageOption !==
      selectedAnswer?.toString()
    ) {
      dispatch(
        updateQuote({
          ...quote,
          attributes: {
            ...quote.attributes,
            paymentDetails: {
              ...(quote.attributes.paymentDetails as PaymentDetails),
              postageOption: selectedAnswer,
            },
          },
        })
      );
    }
  }, [selectedAnswer, application]);

  const checkApplicationBasedLogic = () => {
    if (application === "renewal") {
      return (
        !paymentDetails?.postageOption || paymentDetails?.postageOption === "0"
      );
    }

    // quote
    return (
      !quote?.attributes?.paymentDetails?.postageOption ||
      quote?.attributes?.paymentDetails?.postageOption === "0"
    );
  };

  const buttonClick = (e: number) => {
    if (checkApplicationBasedLogic() && e.toString() === "1") {
      setSelectedAnswer(e.toString());
    } else {
      setSelectedAnswer(e.toString());
    }
  };

  function findAnswerById(id: number) {
    return answerArray.find((answer) => answer.id === id);
  }

  const defaultAnswer = findAnswerById(
    Number(quote?.attributes?.paymentDetails?.postageOption)
  )?.label;

  return (
    <main className="PostalSettings" data-testid="postal-setting-testid">
      <AreYouSurePostalModal
        isOpen={postalSettingModalOpen}
        setIsOpen={(isOpen) => {
          setPostalSettingModalOpen(isOpen);
          if (!isOpen) setSelectedAnswer("1");
        }}
        onChange={(e) => {
          setSelectedAnswer(e.toString());
        }}
        content={{
          areYouSurePopModalTitle,
          areYouSurePopModalContent,
          areYouSurePopModalButtonOne,
          areYouSurePopModalButtonTwo,
        }}
      />
      <div className=" font-body-font ">
        <div
          className=" font-header-font md:text-[25px] text-[23px]
          md:leading-[27.5px] leading-[27.6px] text-text-title  md:text-left mb-spacing-xs"
        >
          {postalTitle}
        </div>
        <div
          className={`leading-[24px] text-[16px] font-normal  ${
            current.uiElementControls?.showPostalOptions
              ? "lg:mb-spacing-l mb-spacing-s"
              : ""
          } font-body-font`}
        >
          {application === "renewal" ? (
            <RichText richText={postalContent} />
          ) : (
            postalContent
          )}
        </div>
      </div>

      {current.uiElementControls?.showPostalOptions && (
        <div
          className={`${Styles.document_wrapper} ${
            brand === BRAND_STAYSURE ? Styles.sts_fix : ""
          } lg:w-full md:w-[400px] w-full lg:mx-auto md:ml-0 mx-auto md:h-[56px]`}
          data-testid="document-type-modal-dropdown"
        >
          <Dropdown
            className={`${Styles.country_select} py-spacing-m py px-spacing-m 
              flex space-between w-full`}
            defaultValue={defaultAnswer}
            strokeColor={getStrokeColor(brand)}
            placeholderText={answerArray[0].label}
            placeholderTextStyle={{
              color: "#272427",
            }}
            listBoxStyle={{
              outline: "none",
              margin: "0px 0px 0px 0px",
              minWidth: "unset !important",
              boxShadow: "0px 0px 12px 0px rgba(15, 23, 42, 0.11)",
            }}
            onSelectionChange={(selectedLabel: string) => {
              const selectedItem = answerArray.find(
                (item) => item.label === selectedLabel
              );

              if (selectedItem) {
                buttonClick(selectedItem.id);
              }
            }}
            unorderedListClassName={`!md:ml-0 !ml-[-1px] !mt-[-4px] !border-border-subdued !rounded-radius-lg 
                    !px-spacing-m !py-spacing-xs`}
            availableListOptionsStyles={{ borderRadius: "4px" }}
            dropdownRef={documentBtnRef}
            shouldCalculateWidth
            isDisabled={isQuoteDisabled}
          >
            {answerArray.map((parent) => (
              <DropdownItem value={parent.label ?? ""} key={parent.id}>
                <span
                  className={`w-full leading-[27px] Proxima Nova text-[18px] font-bold p-spacing-xs 
                        hover:bg-interactive-action-hovered country-name focus:bg-interactive-action-hovered text-text-on-primary`}
                >
                  {parent.label}
                </span>
              </DropdownItem>
            ))}
          </Dropdown>
        </div>
      )}
    </main>
  );
}

export default withBrand(withApplication(PostalSetting));
